import { SignedIn, SignedOut, SignInButton } from "@clerk/clerk-react";
import { motion } from "framer-motion";
import { ArrowRight, Check, ChevronRight, Menu, Minus, X } from "lucide-react";
import React, { useState, useEffect } from "react";
import { Link } from "react-router";
import { type Locale, getTranslationFn } from "../../i18n";
import { cn } from "../../lib/utils";
import * as Paths from "../../paths";
import { FeaturesSectionWithHoverEffects } from "../blocks/feature-section-with-hover-effects";
import { SecuritySection } from "../blocks/security-section";
import { Button } from "../ui/button";
import FAQs from "../ui/faq";
import { FeaturesSectionWithBentoGrid } from "../ui/features-section-with-bento";

interface LandingPageProps {
  locale: Locale;
}

export function LandingPage({ locale }: LandingPageProps) {
  const t = getTranslationFn(locale);
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navItems = [
    { key: "Landing.nav.features" as const, href: "#features" },
    { key: "Landing.nav.security" as const, href: "#security" },
    { key: "Landing.nav.pricing" as const, href: "#pricing" },
    { key: "Landing.nav.testimonials" as const, href: "#testimonials" },
    { key: "Landing.nav.faq" as const, href: "#faq" },
  ];

  type FeatureValue = boolean | string;

  interface PricingFeature {
    name: string;
    category?: string;
    free: FeatureValue;
    plus: FeatureValue;
    pro: FeatureValue;
    enterprise: FeatureValue;
  }

  const pricingFeatures: PricingFeature[] = [
    // Basics
    {
      category: t("Pricing.categoryBasics"),
      name: t("Pricing.featurePrice"),
      free: t("Pricing.free"),
      plus: "199zł",
      pro: "499zł",
      enterprise: t("Pricing.custom"),
    },
    {
      name: t("Pricing.featureMessagesMonth"),
      free: "20",
      plus: "100",
      pro: "500",
      enterprise: t("Pricing.unlimited"),
    },
    {
      name: t("Pricing.featureProjects"),
      free: "10",
      plus: t("Pricing.unlimited"),
      pro: t("Pricing.unlimited"),
      enterprise: t("Pricing.unlimited"),
    },
    // AI & Models
    {
      category: t("Pricing.categoryAI"),
      name: t("Pricing.featureAIModels"),
      free: t("Pricing.advanced"),
      plus: t("Pricing.advanced"),
      pro: t("Pricing.allModels"),
      enterprise: t("Pricing.allModelsBYOK"),
    },
    {
      name: t("Pricing.featureReasoningModes"),
      free: t("Pricing.fast"),
      plus: t("Pricing.allModes"),
      pro: t("Pricing.allModes"),
      enterprise: t("Pricing.allModes"),
    },
    {
      name: t("Pricing.featureCitations"),
      free: true,
      plus: true,
      pro: true,
      enterprise: true,
    },
    // Features
    {
      category: t("Pricing.categoryFeatures"),
      name: t("Pricing.featureTranscriptions"),
      free: false,
      plus: false,
      pro: true,
      enterprise: true,
    },
    {
      name: t("Pricing.featureDocxExport"),
      free: true,
      plus: true,
      pro: true,
      enterprise: true,
    },
    // Integrations
    {
      category: t("Pricing.categoryIntegrations"),
      name: t("Pricing.featureGoogleDrive"),
      free: false,
      plus: false,
      pro: true,
      enterprise: true,
    },
    {
      name: t("Pricing.featureLegalSources"),
      free: false,
      plus: false,
      pro: true,
      enterprise: true,
    },
    {
      name: t("Pricing.featureWebSearch"),
      free: false,
      plus: true,
      pro: true,
      enterprise: true,
    },
    // Security
    {
      category: t("Pricing.categorySecurity"),
      name: t("Pricing.featureSSO"),
      free: false,
      plus: false,
      pro: false,
      enterprise: true,
    },
    {
      name: t("Pricing.featurePrivateCloud"),
      free: false,
      plus: false,
      pro: false,
      enterprise: true,
    },
  ];

  const FeatureCell = ({ value }: { value: FeatureValue }) => {
    if (value === true) {
      return <Check className="h-5 w-5 text-emerald-600 mx-auto" />;
    }
    if (value === false) {
      return <Minus className="h-5 w-5 text-slate-300 mx-auto" />;
    }
    return <span className="text-slate-700 text-sm">{value}</span>;
  };

  return (
    <div className="flex min-h-screen flex-col w-full bg-white font-sans selection:bg-slate-900/10">
      {/* Navigation */}
      <header
        className={cn(
          "fixed w-full top-0 z-50 transition-all duration-300 border-b border-transparent",
          isScrolled
            ? "bg-white/95 backdrop-blur-md border-slate-100 shadow-sm py-3"
            : "bg-white py-5"
        )}
      >
        <div className="container mx-auto px-4 md:px-6 flex items-center justify-between">
          <button
            onClick={() => {
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity"
            aria-label="Scroll to top"
          >
            <img
              src="/assets/logo.svg"
              alt="AnyLawyer Logo"
              className="w-[140px]"
            />
          </button>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8">
            {navItems.map((item) => (
              <Link
                key={item.key}
                to={item.href}
                className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
              >
                {t(item.key)}
              </Link>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-4">
            <SignedIn>
              <Button
                asChild
                className="rounded-lg px-6 bg-[rgb(28_28_28)] text-white hover:bg-[rgb(20_20_20)]"
              >
                <Link to={Paths.app()}>
                  {t("Landing.nav.goToApp")}{" "}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </SignedIn>
            <SignedOut>
              <SignInButton mode="modal">
                <Button
                  variant="ghost"
                  className="rounded-lg text-slate-600 hover:text-slate-900"
                >
                  {t("Landing.nav.login")}
                </Button>
              </SignInButton>
              <SignInButton mode="modal">
                <Button className="rounded-lg px-6 bg-[rgb(28_28_28)] text-white hover:bg-[rgb(20_20_20)] shadow-lg shadow-[rgb(28_28_28)]/20 hover:shadow-[rgb(28_28_28)]/30 transition-all">
                  {t("Landing.nav.startTrial")}
                </Button>
              </SignInButton>
            </SignedOut>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-slate-600"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X /> : <Menu />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 w-full bg-white border-b shadow-lg p-4 flex flex-col gap-4">
            {navItems.map((item) => (
              <Link
                key={item.key}
                to={item.href}
                className="text-base font-medium text-slate-600 py-2 border-b border-slate-50"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t(item.key)}
              </Link>
            ))}
            <div className="flex flex-col gap-3 mt-2">
              <SignedOut>
                <SignInButton mode="modal">
                  <Button
                    variant="outline"
                    className="w-full justify-center rounded-lg"
                  >
                    {t("Landing.nav.login")}
                  </Button>
                </SignInButton>
                <SignInButton mode="modal">
                  <Button className="w-full justify-center rounded-lg bg-[rgb(28_28_28)] text-white hover:bg-[rgb(20_20_20)]">
                    {t("Landing.nav.startTrial")}
                  </Button>
                </SignInButton>
              </SignedOut>
              <SignedIn>
                <Button
                  asChild
                  className="w-full justify-center rounded-lg bg-[rgb(28_28_28)] text-white hover:bg-[rgb(20_20_20)]"
                >
                  <Link to={Paths.app()}>{t("Landing.nav.goToApp")}</Link>
                </Button>
              </SignedIn>
            </div>
          </div>
        )}
      </header>

      <main className="flex-1 pt-20">
        {/* Hero Section */}
        <section className="relative w-full py-20 md:py-32 overflow-hidden bg-white">
          <div className="absolute inset-0 -z-10">
            <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808008_1px,transparent_1px),linear-gradient(to_bottom,#80808008_1px,transparent_1px)] bg-[size:24px_24px]"></div>
            <div className="absolute left-0 right-0 top-0 -z-10 m-auto h-[310px] w-[310px] rounded-full bg-slate-200/20 opacity-20 blur-[100px]"></div>
          </div>

          <div className="container mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
              <div className="flex-1 space-y-8 text-center lg:text-left max-w-2xl lg:max-w-none mx-auto">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-sm font-medium text-slate-600"
                >
                  <span className="flex h-2 w-2 rounded-full bg-green-500 mr-2 animate-pulse"></span>
                  {t("Landing.hero.badge")}
                </motion.div>

                <motion.h1
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.1 }}
                  className="text-5xl md:text-6xl lg:text-7xl font-serif tracking-tight text-slate-900 leading-[1.1]"
                >
                  {t("Landing.hero.title1")} <br />
                  <span className="italic text-slate-800">
                    {t("Landing.hero.title2")}
                  </span>
                </motion.h1>

                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  className="text-xl text-slate-500 max-w-2xl mx-auto lg:mx-0 leading-relaxed font-sans"
                >
                  {t("Landing.hero.description")}
                </motion.p>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                  className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 pt-2"
                >
                  <SignedOut>
                    <SignInButton mode="modal">
                      <Button
                        size="lg"
                        className="h-12 px-8 text-base font-medium rounded-lg bg-[rgb(28_28_28)] text-white shadow-xl shadow-[rgb(28_28_28)]/10 hover:shadow-[rgb(28_28_28)]/20 transition-all hover:-translate-y-0.5 hover:bg-[rgb(20_20_20)]"
                      >
                        {t("Landing.nav.startTrial")}
                        <ChevronRight className="ml-2 h-4 w-4" />
                      </Button>
                    </SignInButton>
                  </SignedOut>
                  <SignedIn>
                    <Button
                      size="lg"
                      asChild
                      className="h-12 px-8 text-base font-medium rounded-lg bg-[rgb(28_28_28)] text-white shadow-xl shadow-[rgb(28_28_28)]/10 hover:shadow-[rgb(28_28_28)]/20 transition-all hover:-translate-y-0.5 hover:bg-[rgb(20_20_20)]"
                    >
                      <Link to={Paths.app()}>
                        {t("Landing.nav.goToApp")}
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Link>
                    </Button>
                  </SignedIn>
                  <Button
                    variant="outline"
                    size="lg"
                    asChild
                    className="h-12 px-8 text-base font-medium rounded-lg border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                  >
                    <Link to="mailto:hello@anylawyer.com">
                      {t("Landing.hero.bookDemo")}
                    </Link>
                  </Button>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 1, delay: 0.6 }}
                  className="pt-6 flex flex-wrap items-center justify-center lg:justify-start gap-x-8 gap-y-4 text-sm text-slate-500"
                >
                  <div className="flex items-center gap-2">
                    <div className="flex h-5 w-5 items-center justify-center rounded-full bg-slate-100">
                      <Check className="h-3 w-3 text-slate-600" />
                    </div>
                    <span>{t("Landing.hero.feature1")}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex h-5 w-5 items-center justify-center rounded-full bg-slate-100">
                      <Check className="h-3 w-3 text-slate-600" />
                    </div>
                    <span>{t("Landing.hero.feature2")}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex h-5 w-5 items-center justify-center rounded-full bg-slate-100">
                      <Check className="h-3 w-3 text-slate-600" />
                    </div>
                    <span>{t("Landing.hero.feature3")}</span>
                  </div>
                </motion.div>
              </div>

              <div className="flex-1 relative lg:h-[600px] w-full flex items-center justify-center">
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.7, delay: 0.2 }}
                  className="relative w-full aspect-square max-w-[600px]"
                >
                  {/* Abstract UI Representation */}
                  <img
                    src="/assets/side-by-side.png"
                    alt="AnyLawyer Interface"
                    className="relative z-10 w-full rounded-xl shadow-2xl border border-slate-200/60"
                  />
                </motion.div>
              </div>
            </div>
          </div>
        </section>

        {/* Trusted By */}
        <section className="border-y border-slate-100 bg-white py-10">
          <div className="container mx-auto px-4 text-center">
            <p className="text-sm font-medium text-slate-500 uppercase tracking-widest mb-8">
              {t("Landing.trustedBy")}
            </p>
            <div className="flex flex-wrap justify-center items-center gap-12 md:gap-20 opacity-60 grayscale hover:grayscale-0 transition-all duration-500">
              {/* Placeholder Logos - keeping the existing one and adding generic placeholders if needed */}
              <img
                src="https://dsk-kancelaria.pl/wp-content/themes/starter-empressia/dist/images/dsk-logo-dark.svg"
                alt="DSK Law Firm"
                className="h-12 object-contain"
              />
              {/* You would add more client logos here */}
            </div>
          </div>
        </section>

        {/* Bento Grid Features */}
        <section id="features" className="py-24 md:py-32 bg-white relative">
          <FeaturesSectionWithBentoGrid locale={locale} />
        </section>

        {/* Additional Capabilities */}
        <section className="py-24 bg-slate-50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-slate-900 mb-4">
                {t("Landing.toolkit.title")}
              </h2>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                {t("Landing.toolkit.description")}
              </p>
            </div>
            <FeaturesSectionWithHoverEffects locale={locale} />
          </div>
        </section>

        {/* Security Section */}
        <SecuritySection locale={locale} />

        {/* Pricing Section */}
        <section
          id="pricing"
          className="py-32 bg-slate-50/50 border-t border-slate-100"
        >
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-slate-900 mb-6">
                {t("Landing.pricing.title")}
              </h2>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
                {t("Landing.pricing.description")}
              </p>
            </div>

            {/* Pricing Table */}
            <div className="max-w-6xl mx-auto overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[640px]">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="px-6 py-5 text-left text-sm font-semibold text-slate-900 w-[200px]">
                        {t("Pricing.features")}
                      </th>
                      <th className="px-4 py-5 text-center">
                        <div className="text-sm font-semibold text-slate-900">
                          {t("Pricing.planFree")}
                        </div>
                        <div className="mt-1 text-2xl font-bold text-slate-900">
                          0zł
                        </div>
                        <div className="text-xs text-slate-500">
                          {t("Pricing.trialPeriod")}
                        </div>
                      </th>
                      <th className="px-4 py-5 text-center">
                        <div className="text-sm font-semibold text-slate-900">
                          {t("Pricing.planPlus")}
                        </div>
                        <div className="mt-1 text-2xl font-bold text-slate-900">
                          199zł
                        </div>
                        <div className="text-xs text-slate-500">
                          {t("Pricing.perSeatMonth")}
                        </div>
                      </th>
                      <th className="px-4 py-5 text-center bg-slate-900 text-white rounded-t-lg">
                        <div className="text-sm font-semibold">
                          {t("Pricing.planPro")}
                        </div>
                        <div className="mt-1 text-2xl font-bold">499zł</div>
                        <div className="text-xs text-slate-400">
                          {t("Pricing.perSeatMonth")}
                        </div>
                      </th>
                      <th className="px-4 py-5 text-center">
                        <div className="text-sm font-semibold text-slate-900">
                          {t("Pricing.planEnterprise")}
                        </div>
                        <div className="mt-1 text-2xl font-bold text-slate-900">
                          {t("Pricing.custom")}
                        </div>
                        <div className="text-xs text-slate-500">
                          {t("Pricing.contactUs")}
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      let currentCategory = "";
                      return pricingFeatures.map((feature, idx) => {
                        const showCategory =
                          feature.category &&
                          feature.category !== currentCategory;
                        if (feature.category) {
                          currentCategory = feature.category;
                        }

                        return (
                          <React.Fragment key={idx}>
                            {showCategory && (
                              <tr className="bg-slate-50">
                                <td
                                  colSpan={5}
                                  className="px-6 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider"
                                >
                                  {feature.category}
                                </td>
                              </tr>
                            )}
                            <tr className="border-t border-slate-100 hover:bg-slate-50/50 transition-colors">
                              <td className="px-6 py-3 text-sm text-slate-700">
                                {feature.name}
                              </td>
                              <td className="px-4 py-3 text-center">
                                <FeatureCell value={feature.free} />
                              </td>
                              <td className="px-4 py-3 text-center">
                                <FeatureCell value={feature.plus} />
                              </td>
                              <td className="px-4 py-3 text-center bg-slate-50/50">
                                <FeatureCell value={feature.pro} />
                              </td>
                              <td className="px-4 py-3 text-center">
                                <FeatureCell value={feature.enterprise} />
                              </td>
                            </tr>
                          </React.Fragment>
                        );
                      });
                    })()}
                  </tbody>
                </table>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="mt-10 text-center">
              <p className="text-slate-600 mb-4">{t("Pricing.ctaText")}</p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <SignedOut>
                  <SignInButton mode="modal">
                    <Button className="h-12 px-8 text-base font-medium rounded-lg bg-slate-900 text-white hover:bg-slate-800">
                      {t("Pricing.ctaStartTrial")}
                    </Button>
                  </SignInButton>
                </SignedOut>
                <SignedIn>
                  <Button
                    asChild
                    className="h-12 px-8 text-base font-medium rounded-lg bg-slate-900 text-white hover:bg-slate-800"
                  >
                    <Link to={Paths.app()}>
                      {t("Landing.pricing.getStarted")}
                    </Link>
                  </Button>
                </SignedIn>
                <Button
                  asChild
                  variant="outline"
                  className="h-12 px-8 text-base font-medium rounded-lg"
                >
                  <Link to="mailto:hello@anylawyer.com">
                    {t("Pricing.ctaContactSales")}
                  </Link>
                </Button>
              </div>

              {/* Footnote */}
              <p className="mt-8 text-sm text-slate-500">
                <span className="font-medium">*</span>{" "}
                <span className="font-medium">
                  {t("Pricing.featureLegalSources")}
                </span>{" "}
                {t("Pricing.legalSourcesFootnote")}
              </p>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <FAQs locale={locale} />

        {/* Final CTA */}
        <section className="py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="bg-[rgb(28_28_28)] p-12 md:p-20 text-center relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-full z-0">
                <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[rgb(20_20_20)]/30 rounded-full blur-[100px]"></div>
                <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-[rgb(20_20_20)]/30 rounded-full blur-[100px]"></div>
              </div>
              <div className="relative z-10 max-w-3xl mx-auto space-y-8">
                <h2 className="text-3xl md:text-5xl font-bold text-white tracking-tight">
                  {t("Landing.cta.title")}
                </h2>
                <p className="text-xl text-slate-300">
                  {t("Landing.cta.description")}
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <SignedOut>
                    <SignInButton mode="modal">
                      <Button
                        size="lg"
                        className="h-14 px-10 text-lg rounded-lg bg-white hover:bg-slate-100 text-slate-900 font-medium"
                      >
                        {t("Landing.nav.startTrial")}
                      </Button>
                    </SignInButton>
                  </SignedOut>
                  <SignedIn>
                    <Button
                      size="lg"
                      asChild
                      className="h-14 px-10 text-lg rounded-lg bg-white hover:bg-slate-100 text-slate-900 font-medium"
                    >
                      <Link to={Paths.app()}>{t("Landing.nav.goToApp")}</Link>
                    </Button>
                  </SignedIn>
                  <Button
                    size="lg"
                    variant="outline"
                    className="h-14 px-10 text-lg rounded-lg bg-transparent text-white border-slate-600 hover:bg-slate-800 hover:text-white"
                  >
                    {t("Landing.pricing.contactSales")}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-white border-t border-slate-100 py-12">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-4 gap-12 mb-12">
              <div className="col-span-1 md:col-span-1">
                <img src="/assets/logo.svg" alt="Logo" className="w-32 mb-6" />
                <p className="text-slate-500 text-sm">
                  {t("Landing.footer.tagline")}
                </p>
              </div>
              <div>
                <h4 className="font-bold text-slate-900 mb-4">
                  {t("Landing.footer.product")}
                </h4>
                <ul className="space-y-2 text-sm text-slate-600">
                  <li>
                    <Link to="#features" className="hover:text-slate-900">
                      {t("Landing.nav.features")}
                    </Link>
                  </li>
                  <li>
                    <Link to="#security" className="hover:text-slate-900">
                      {t("Landing.nav.security")}
                    </Link>
                  </li>
                  <li>
                    <Link to="#" className="hover:text-slate-900">
                      {t("Landing.nav.pricing")}
                    </Link>
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold mb-4">
                  {t("Landing.footer.legal")}
                </h3>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li>
                    <Link
                      to={Paths.privacyPolicy()}
                      className="hover:text-primary transition-colors"
                    >
                      {t("Landing.footer.privacyPolicy")}
                    </Link>
                  </li>
                  <li>
                    <Link
                      to={Paths.termsOfService()}
                      className="hover:text-primary transition-colors"
                    >
                      {t("Landing.footer.termsOfService")}
                    </Link>
                  </li>
                </ul>
                <div className="mt-6">
                  <h3 className="font-semibold mb-2">
                    {t("Landing.footer.contactUs")}
                  </h3>
                  <Link
                    to="mailto:hello@anylawyer.com"
                    className="text-sm text-primary hover:underline"
                  >
                    hello@anylawyer.com
                  </Link>
                </div>
              </div>
            </div>
            <div className="border-t border-slate-100 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-sm text-slate-400">
                {t("Landing.footer.copyright")}
              </p>
              <div className="flex gap-4">
                {/* Social icons would go here */}
              </div>
            </div>
          </div>
        </footer>
      </main>
    </div>
  );
}
